<template>
    <div>
        <v-card class="default_card_wm elevation-0 px-4 py-3 mb-2">
            <v-row no-gutters class="d-flex align-center">
                <v-col cols="9" class="d-flex align-center">
                    <v-btn-icon
                        @click="showData = !showData"
                        :icon="showData ? '$fa-chevron-up' : '$fa-chevron-down'"
                    />
                    <h3 class="ml-4">Informations bénéficiaires</h3>
                </v-col>
                <v-col cols="3" class="d-flex justify-end">
                    <v-btn rounded size="small" @click="addManualRow()" v-if="showData">
                        Ajouter une ligne
                    </v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters v-if="loading">
                <v-col cols="12" class="d-flex" style="height: 432px">
                    <v-progress-circular indeterminate size="28" class="mx-auto" color="primary" />
                </v-col>
            </v-row>
            <v-row no-gutters v-else-if="isIncomingOutgoingPaymentAccountingType">
                <v-col cols="12" class="ml-4">
                    En comptabilité de trésorerie, il n'y a pas de récupération des montants depuis
                    la comptabilité, la DAS2 est à remplir manuellement.
                </v-col>
            </v-row>
            <v-row no-gutters v-else>
                <v-col cols="12" class="ml-4">
                    Les racines de comptes utilisées par l'assistant sont les suivantes : 604, 622
                    et 651.
                </v-col>
            </v-row>
            <v-row no-gutters v-if="!items || !items.length">
                <v-col cols="12" class="ml-4 mt-4">
                    <b>Aucune donnée trouvée.</b>
                </v-col>
            </v-row>
            <template v-if="showData">
                <div v-for="(item, index) of items" no-gutters class="px-4 py-4" :key="index">
                    <v-row no-gutters class="d-flex align-center py-1">
                        <v-col cols="4" class="d-flex align-center">
                            <v-btn-icon
                                @click="item.toggleExpand()"
                                size="small"
                                :icon="item.isExpanded ? '$fa-chevron-up' : '$fa-chevron-down'"
                            />
                            <b>{{ item.thirdPartyCompanyName }}</b>
                        </v-col>
                        <v-col cols="2" class="px-1">
                            <v-text-field-filled
                                v-model="item.debit"
                                label="Montant TTC"
                                outlined
                                hide-details="auto"
                                density="compact"
                                :append-inner-icon="item.id && '$oss-eye'"
                                @click:append-inner="openDetailDialog(item)"
                                :rules="[(value: string) => noDecimalAndMinRule(value)]"
                            />
                        </v-col>
                        <v-col cols="2" class="px-1">
                            <v-select-filled
                                v-model="item.type"
                                label="Nature"
                                :items="itemTypes"
                                hide-details
                                outlined
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="2" class="px-1">
                            <v-text-field-filled
                                v-model="item.profession"
                                label="Profession"
                                outlined
                                hide-details="auto"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="2" class="d-flex align-center justify-center">
                            <v-switch v-model="item.toAdd" @click="item.toggleAdd()" />
                            <p class="ml-3">A déclarer</p>
                        </v-col>
                    </v-row>
                    <template v-if="item.isExpanded">
                        <v-row no-gutters class="px-1 py-1">
                            <v-col cols="3" class="px-1">
                                <v-checkbox
                                    v-model="item.naturalPerson"
                                    label="Personne physique"
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                            <v-col cols="3" class="px-1">
                                <v-text-field-filled
                                    v-model="item.thirdPartyCompanyName"
                                    :label="item.naturalPerson ? 'Nom Prénom' : 'Raison sociale'"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                            <v-col cols="3" class="px-1">
                                <v-text-field-filled
                                    v-model="item.siret"
                                    label="SIRET"
                                    @input="item.siret = onInputSetTruncatedSiret(item.siret)"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                            <v-col cols="3" class="px-1">
                                <v-text-field-filled
                                    v-model="item.legalForm"
                                    label="Forme juridique"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="px-1 py-1">
                            <v-col cols="1" class="px-1">
                                <v-text-field-filled
                                    v-model="item.number"
                                    label="Numéro"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                            <v-col cols="1" class="px-1">
                                <v-text-field-filled
                                    v-model="item.btq"
                                    label="BTQ"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                            <v-col cols="3" class="px-1">
                                <v-text-field-filled
                                    v-model="item.street"
                                    label="Voie"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                            <v-col cols="2" class="px-1">
                                <v-text-field-filled
                                    v-model="item.city"
                                    label="Commune"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                    @update:modelValue="onPostalCodeOrCityChange(item)"
                                />
                            </v-col>
                            <v-col cols="1" class="px-1">
                                <v-text-field-filled
                                    v-model="item.postalCode"
                                    label="Code postal"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                    @update:modelValue="onPostalCodeOrCityChange(item)"
                                />
                            </v-col>
                            <v-col cols="2" class="px-1">
                                <v-text-field-filled
                                    v-model="item.inseeCode"
                                    label="Code Insee commune"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                            <v-col cols="2" class="px-1">
                                <v-text-field-filled
                                    v-model="item.postOfficeDistributor"
                                    label="Bureau distributeur"
                                    outlined
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                        </v-row>
                    </template>
                    <v-row v-if="index < items.length - 1">
                        <v-divider></v-divider>
                    </v-row>
                </div>
            </template>
        </v-card>
        <oss-dialog
            :title="selectedItem && `Détail - ${selectedItem.thirdPartyCompanyName}`"
            v-model="showBalanceDetailDialog"
            @valid="closeDetailDialog()"
            @close="closeDetailDialog()"
            validTitle="Fermer"
        >
            <div no-gutters style="width: 75vw" class="text-center">
                <AccountableAccountBalanceDetail
                    :companyId="companyId"
                    :accountableAccountInfo="accountableAccountInfo"
                    :startDate="startDate"
                    :endDate="endDate"
                />
            </div>
        </oss-dialog>
    </div>
</template>

<script lang="ts">
import { Component, Vue, toNative, Prop } from "vue-facing-decorator";
import { DasTwoBeneficiary } from "@/services/declaration/declarationServices";
import AccountableAccountBalanceDetail from "@/views/AccountableAccountBalanceDetail.vue";
import { truncateInput } from "@/helpers/formInput.helper";

@Component({
    components: {
        AccountableAccountBalanceDetail,
    },
})
export class DasTwoBeneficiariesCard extends Vue {
    @Prop({ default: null }) items: DasTwoBeneficiary[];
    @Prop({ default: null }) loading: boolean;
    @Prop({ default: null }) companyId: string;
    @Prop({ default: null }) startDate: string;
    @Prop({ default: null }) endDate: string;
    @Prop({ default: false }) isIncomingOutgoingPaymentAccountingType: boolean;

    showData: boolean = true;

    showBalanceDetailDialog: boolean = false;

    itemTypes: Array<{ value: string; title: string }> = [
        { value: "AR", title: "Autres rémunérations" },
        { value: "C", title: "Commission" },
        { value: "CO", title: "Courtage" },
        { value: "DA", title: "Droits d'auteur" },
        { value: "DI", title: "Droits d'inventeur" },
        { value: "H", title: "Honoraire, vacation" },
        { value: "JP", title: "Jetons de présence" },
        { value: "R", title: "Ristourne" },
    ];

    selectedItem: DasTwoBeneficiary = null;

    get accountableAccountInfo() {
        return {
            name: this.selectedItem?.accountableAccountName,
            thirdPartyCompanyName: this.selectedItem?.thirdPartyCompanyName,
            thirdPartyCompanyId: this.selectedItem?.id,
        };
    }

    addManualRow() {
        const newItem = new DasTwoBeneficiary(
            "",
            "",
            0,
            "Nouveau bénéficiaire",
            "",
            "",
            "",
            "",
            "",
        );
        this.items.splice(0, 0, newItem);
    }

    openDetailDialog(item: DasTwoBeneficiary) {
        this.selectedItem = item;
        this.showBalanceDetailDialog = true;
    }

    onInputSetTruncatedSiret(siret: string): string {
        return truncateInput(siret, 14);
    }

    closeDetailDialog() {
        this.selectedItem = null;
        this.showBalanceDetailDialog = false;
    }

    noDecimalAndMinRule(value: string): boolean | string {
        const isInteger = /^\d+$/.test(value);
        if (!isInteger) return "Nombre entier attendu";
        if (+value <= 2400) return "Montant supérieur à 2400 € attendu";
        return true;
    }

    async onPostalCodeOrCityChange(item: DasTwoBeneficiary) {
        await item.getCityInseeCode();
    }
}

export default toNative(DasTwoBeneficiariesCard);
</script>
